import { DestroyRef, ElementRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { InboxDto } from '@app/core/model';
import { AuthorizationApiService, LookupService } from '@app/core/service';
import { FormDialogComponent } from '@app/shared/components';
import { FormDialogConfig, InboundFaxesTableModel } from '@app/shared/models';
import { DialogConfig } from '@app/shared/utility';
import { InboundFaxAction } from '@mynexus/mynexus-ui-lib';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, firstValueFrom, of } from 'rxjs';

@Injectable()
export class AddLineService {
  private readonly authorizationApiService = inject(AuthorizationApiService);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly lookupService = inject(LookupService);
  private readonly ref = inject(ElementRef);

  public async handleClick(target: InboundFaxesTableModel, inbox: InboxDto) {
    const formDialogConfigs = await this.getFormDialogConfigs(target, inbox);

    this.dialogService
      .open(
        FormDialogComponent,
        DialogConfig('Add New Line', {
          submitLabel: 'Next',
          formDialogConfigs: of(formDialogConfigs),
          callback: (authorizationNumber: string) =>
            this.validateAuthNumber(inbox.tenantLobProduct.tenantLobProductId, target.memberId, authorizationNumber),
        })
      )
      .onClose.subscribe((res: object) => {
        if (res) {
          const data: InboundFaxAction = {
            authorizationNumber: res['authorizationNumber'],
            description: res['description'],
            documentType: res['documentType'],
            faxReceivedDateTime: target.faxReceivedDateTime,
            fileName: target.documentStorageLocation,
            filePath: `${inbox.basePath}/${target.documentStorageLocation}`,
            fileSizeBytes: target.fileSizeBytes,
            inboundFaxId: target.inboundFaxId,
            memberId: res['memberId'],
            memberSubscriberNumber: target.memberSubscriberNumber,
            productName: inbox.tenantLobProduct.productShortName,
            tenantId: inbox.tenantLobProduct.tenantId,
            tenantLobProductId: inbox.tenantLobProduct.tenantLobProductId,
          };

          this.navigateToAuthorization(data);
        }
      });
  }

  private async getFormDialogConfigs(target: InboundFaxesTableModel, inbox: InboxDto): Promise<FormDialogConfig[]> {
    const documentTypes = (await this.lookupService.getDocumentTypes(inbox)).line.map((x) => {
      return { label: x.description, value: x.description };
    });

    return [
      {
        label: 'Member ID:',
        type: 'text',
        formControlName: 'memberId',
        text: target.memberSubscriberNumber,
      },
      {
        label: 'Name:',
        type: 'text',
        formControlName: 'memberName',
        text: target.memberFullName,
      },
      {
        label: 'Date of Birth:',
        type: 'text',
        formControlName: 'dateOfBirth',
        text: target.memberDOB,
      },
      {
        label: 'Authorization Number:',
        type: 'auth-input',
        formControlName: 'authorizationNumber',
        validators: [
          Validators.required,
          Validators.minLength(15),
          Validators.maxLength(50),
          Validators.pattern('^[a-zA-Z0-9]*$'),
        ],
      },
      {
        label: 'Document Type:',
        type: 'multiselect',
        formControlName: 'documentType',
        options: documentTypes,
        validators: [Validators.required],
      },
      {
        label: 'Description:',
        type: 'input',
        formControlName: 'description',
        charCounter: true,
        maxLength: 40,
      },
    ];
  }

  public validateAuthNumber(tenantLobProductId: string, memberId: string, authNumber: string): Promise<boolean> {
    return firstValueFrom(
      this.authorizationApiService.isValidAuthorization(tenantLobProductId, memberId, authNumber).pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          return of(false);
        })
      )
    );
  }

  public navigateToAuthorization(data: InboundFaxAction): void {
    this.ref.nativeElement.dispatchEvent(
      new CustomEvent('handleServiceLineCreation', {
        bubbles: true,
        detail: { data },
      })
    );
  }
}
